// src/client/renderer/vfx/env/awards-spotlight-pool.ts
//
// Plan 18, Task 5.1 — Awards spotlight ceremony pool.
//
// Behaviour contract:
//   - CEREMONY-mode pool. mode: 'ceremony'. Triggered by AwardRevealedEvent.
//   - SINGLE-INSTANCE: only one award is unfurled at a time. New acquire while
//     active releases the old emitter first (F6 re-entry guard).
//   - Emitter: THREE.Group containing:
//       - THREE.SpotLight at (0, 8, 0), angle ~0.35 rad, aimed at origin
//       - THREE.Mesh (ConeGeometry, additive material) — visible beam volume
//   - Animation (gsap timeline, ~3-second life):
//       cone scale 0.0 → 1.0 over 250ms, hold 2200ms, fade-out 1.0 → 0.0 over 550ms
//       SpotLight intensity follows same envelope
//       tl.call(fn, undefined, 3.0) marks completion
//   - isFinished returns true once the timeline completes.
//   - release(emitter) removes SpotLight + cone volume from scene.
//   - dispose() disposes shared cone geometry + material once.

import * as THREE from 'three';
import type {
  EnvCeremonyPool,
  EnvCeremonyPoolDeps,
  EnvCeremonySpawnArgs,
  EnvCeremonyPoolId,
} from './env-ceremony-types';
import type { CeremonyEvent } from './ceremony-events';
import type { GsapRegistry } from '../gsap-registry';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** SpotLight position Y (directly above podium). */
const SPOTLIGHT_Y = 8;

/** SpotLight cone angle (radians). */
const SPOTLIGHT_ANGLE = 0.35;

/** SpotLight penumbra softness [0,1]. */
const SPOTLIGHT_PENUMBRA = 0.3;

/** SpotLight peak intensity. */
const SPOTLIGHT_PEAK_INTENSITY = 4.0;

/** Cone geometry dimensions: base radius, height. Cone narrows at top (Y=8). */
const CONE_RADIUS_BASE = 3.0;
const CONE_HEIGHT = 8.0;
const CONE_SEGMENTS = 16;

/** Timeline phases (seconds). */
const PHASE_FADE_IN = 0.25;
const PHASE_HOLD = 2.2;
const PHASE_FADE_OUT = 0.55;
const LIFETIME = 3.0; // PHASE_FADE_IN + PHASE_HOLD + PHASE_FADE_OUT = 3.0

// ─────────────────────────────────────────────────────────────────────────────
// Per-emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface SpotlightEmitterState {
  group: THREE.Group;
  spotlight: THREE.SpotLight;
  coneMesh: THREE.Mesh;
  tlHandle: ReturnType<GsapRegistry['create']>;
  elapsed: number;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createAwardsSpotlightPool({
  scene,
  registry,
}: EnvCeremonyPoolDeps): EnvCeremonyPool {
  /** Shared cone geometry — disposed once in dispose(). */
  const sharedConeGeometry = new THREE.ConeGeometry(CONE_RADIUS_BASE, CONE_HEIGHT, CONE_SEGMENTS);

  /** Shared cone material template — cloned per emitter if needed; disposed once in dispose(). */
  const sharedConeMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffcc,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });

  // Single-instance state
  let currentEmitter: THREE.Object3D | null = null;
  let currentState: SpotlightEmitterState | null = null;
  let disposed = false;

  // ─────────────────────────────────────────────────────────────────────────
  // Internal helpers
  // ─────────────────────────────────────────────────────────────────────────

  function releaseState(state: SpotlightEmitterState): void {
    state.tlHandle.dispose();
    scene.remove(state.group);
    scene.remove(state.spotlight.target);
    // Dispose per-emitter cone material clone
    (state.coneMesh.material as THREE.Material).dispose();
    state.finished = true;
  }

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(_args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // F6 re-entry guard: release existing emitter before creating a new one
    if (currentEmitter !== null && currentState !== null) {
      releaseState(currentState);
      currentEmitter = null;
      currentState = null;
    }

    // ── Build SpotLight ────────────────────────────────────────────────────
    const spotlight = new THREE.SpotLight(0xffffff, 0, 0, SPOTLIGHT_ANGLE, SPOTLIGHT_PENUMBRA, 1);
    spotlight.position.set(0, SPOTLIGHT_Y, 0);
    // Spotlight target at origin so it points straight down
    spotlight.target.position.set(0, 0, 0);
    scene.add(spotlight.target);

    // ── Build cone volume ──────────────────────────────────────────────────
    // Clone material so each emitter has its own opacity channel
    const coneMaterial = sharedConeMaterial.clone();
    coneMaterial.opacity = 0;

    const coneMesh = new THREE.Mesh(sharedConeGeometry, coneMaterial);
    // Position cone so its tip is at Y=8 and base at Y=0 (pointing down)
    // ConeGeometry by default has its tip at +Y and base at -Y (centered at origin)
    // We want tip at Y=8 and base at Y=0, so center is at Y=4
    coneMesh.position.set(0, SPOTLIGHT_Y - CONE_HEIGHT / 2, 0);
    // Rotate 180° around Z so the tip points up (towards the spotlight source)
    // Actually: ConeGeometry apex is at Y=+height/2, base at Y=-height/2
    // We want apex at Y=8 (spotlight source) and base opening at Y=0
    // So apex at local Y = +CONE_HEIGHT/2, center at Y=4 → apex world Y = 4 + 4 = 8 ✓
    // No rotation needed — default orientation has apex pointing up

    // Start at zero scale for the fade-in animation
    coneMesh.scale.set(0, 0, 0);

    // ── Build Group ────────────────────────────────────────────────────────
    const group = new THREE.Group();
    group.add(spotlight);
    group.add(coneMesh);
    scene.add(group);

    // ── Build gsap timeline ────────────────────────────────────────────────
    const tlHandle = registry.create('awards_spotlight');
    const tl = tlHandle.timeline;

    // Intensity proxy (GSAP cannot animate SpotLight.intensity directly in some envs)
    const intensityProxy = { value: 0 };

    // Phase 1: fade in (0 → PHASE_FADE_IN seconds)
    // Scale: 0 → 1
    tl.to(
      coneMesh.scale,
      {
        x: 1,
        y: 1,
        z: 1,
        duration: PHASE_FADE_IN,
        ease: 'power2.out',
      },
      0,
    );
    // Opacity: 0 → 0.5
    tl.to(
      coneMaterial,
      {
        opacity: 0.5,
        duration: PHASE_FADE_IN,
        ease: 'power2.out',
      },
      0,
    );
    // Intensity: 0 → peak
    tl.to(
      intensityProxy,
      {
        value: SPOTLIGHT_PEAK_INTENSITY,
        duration: PHASE_FADE_IN,
        ease: 'power2.out',
        onUpdate: () => {
          spotlight.intensity = intensityProxy.value;
        },
      },
      0,
    );

    // Phase 2: hold (PHASE_FADE_IN → PHASE_FADE_IN + PHASE_HOLD)
    // No explicit tween needed — previous tweens end here; next begin after
    // Use a zero-opacity tween at position PHASE_FADE_IN to signal hold start
    // (GSAP timeline naturally holds the last values)

    // Phase 3: fade out (PHASE_FADE_IN + PHASE_HOLD → LIFETIME)
    const fadeOutStart = PHASE_FADE_IN + PHASE_HOLD;

    tl.to(
      coneMesh.scale,
      {
        x: 0,
        y: 0,
        z: 0,
        duration: PHASE_FADE_OUT,
        ease: 'power2.in',
      },
      fadeOutStart,
    );
    tl.to(
      coneMaterial,
      {
        opacity: 0,
        duration: PHASE_FADE_OUT,
        ease: 'power2.in',
      },
      fadeOutStart,
    );
    tl.to(
      intensityProxy,
      {
        value: 0,
        duration: PHASE_FADE_OUT,
        ease: 'power2.in',
        onUpdate: () => {
          spotlight.intensity = intensityProxy.value;
        },
      },
      fadeOutStart,
    );

    // Completion marker at absolute 3.0s (NEVER eventCallback)
    const state: SpotlightEmitterState = {
      group,
      spotlight,
      coneMesh,
      tlHandle,
      elapsed: 0,
      finished: false,
    };

    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    currentEmitter = group;
    currentState = state;

    return group;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed) return new Set();

    if (currentState && currentEmitter) {
      currentState.elapsed += deltaSeconds;
      // Advance gsap timeline manually
      currentState.tlHandle.timeline.time(currentState.elapsed);

      if (currentState.elapsed >= LIFETIME || currentState.finished) {
        currentState.finished = true;
      }

      return new Set([currentEmitter]);
    }

    return new Set();
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    if (emitter !== currentEmitter) {
      // Either a stale ref (released) or an unknown object — treat as finished
      return true;
    }
    return currentState ? currentState.finished : true;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (emitter !== currentEmitter) return; // safe no-op for stale/unknown refs
    if (currentState) {
      releaseState(currentState);
    }
    currentEmitter = null;
    currentState = null;
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (currentState) {
      releaseState(currentState);
      currentEmitter = null;
      currentState = null;
    }

    // Dispose shared resources exactly once
    sharedConeGeometry.dispose();
    sharedConeMaterial.dispose();
  }

  return { mode: 'ceremony', acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a CeremonyEvent to awards-spotlight spawn args.
 * Returns null for any event other than award_revealed.
 */
export function resolveAwardsSpotlightForEvent(
  event: CeremonyEvent,
): { poolId: EnvCeremonyPoolId; args: EnvCeremonySpawnArgs } | null {
  if (event.type !== 'award_revealed') return null;

  return {
    poolId: 'awards_spotlight',
    args: {
      category: event.category,
      outcome: event.outcome,
    },
  };
}
